import { useMemo, type ReactNode } from "react";
import { useGetIdentity, useGetList, useTranslate } from "ra-core";

import { useIsMobile } from "@/hooks/use-mobile";

import {
  CLOSED_WON_STAGE,
  DIRECT_SALE_LEAD_TYPES,
  selectDirectSalesInPeriod,
  sumDirectSalesAmountMxn,
} from "../incentives/directSales";
import { formatMxn } from "../incentives/IncentiveBars";
import { computeAgencyGoalProgress } from "../incentives/incentiveProgress";
import { APP_LOCALE } from "../misc/locale";
import {
  daysLeftInPeriod,
  getCurrentMonthPeriod,
  periodMonthKey,
} from "../incentives/periods";
import { ListErrorState, ListRowsSkeleton } from "../design/ListStates";
import { MonthBar } from "../design/MonthBar";
import { ProgressBar } from "../design/ProgressBar";
import { MobileContent } from "../layout/MobileContent";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type {
  AdvisorGoal,
  Contact,
  ContactNote,
  Deal,
  Project,
} from "../types";
import { DashboardStepper } from "./DashboardStepper";
import { DireccionAlertsSection } from "./DireccionAlertsSection";
import { DireccionFunnelSection } from "./DireccionFunnelSection";
import { MobileHomeHeader } from "./MobileDashboard";
import { PipelineTotals } from "./PipelineTotalsSection";
import { PreparedSlots } from "./PreparedSlots";
import {
  buildPipelineTotals,
  buildProjectSales,
  computePosiCompletePercent,
  mergeExactStageCounts,
  selectDisplayedPipelineTotals,
} from "./pipelineTotals";
import { useStageOpenLeadCounts } from "./useStageOpenLeadCounts";

/**
 * Almikia Epic E7 (TASK-094): direccion's (and, with the same panel minus the
 * VoBo slot, a lead_manager's) home (`docs/almikia/design/B-direccion`) --
 * "para llegar a decisiones sin pedir un reporte" (design-system.md section
 * 2). Wired into `RoleHome.tsx` for both shells; renders identically on
 * desktop and mobile, like every other E7 home. Every figure comes from a
 * request the signed-in role's own RLS (Supabase) or FakeRest's unfiltered
 * demo read already narrows -- a lead_manager's numbers are scoped to her
 * own brazo automatically, never by a client-side filter added here.
 *
 * NOT built here (ASSUMPTION, ticket description): the "Salud del negocio"
 * ScoreRing, "Comisiones cobradas", "Leads contestados en <15 min", "Leads
 * atendidos en 24 h" and "Pulso del equipo" -- their data (operations,
 * collections, first-response times) does not exist yet.
 */

// Generous, fixed caps (mirrors `useAdvisorProgress.ts`'s per-query cap
// convention -- see `pipelineTotals.ts`'s module docstring for why a single
// bounded read stands in for a true count-only aggregate here). 1,000
// matches the server's own row cap (`supabase/config.toml`'s `max_rows`,
// 1,000 by default on hosted Supabase too) -- raising this constant alone
// would NOT raise the real limit, so a real agency past 1,000 open leads or
// 1,000 all-time closed sales gets a visible "partial data" note
// (`isPipelineAmountPartial` below) rather than a silently truncated MXN
// total. Per-stage COUNTS are exact regardless of this cap --
// `useStageOpenLeadCounts` reads each stage's own `total` from a `perPage: 1`
// query, not from this bounded list.
const MAX_OPEN_LEADS = 1000;
const MAX_DIRECT_SALE_DEALS = 1000;
const MAX_GOALS = 500;
const MAX_PROJECTS = 50;

const MONTH_YEAR_FORMAT = new Intl.DateTimeFormat(APP_LOCALE, {
  month: "long",
  year: "numeric",
});

const MONTH_DATE_FORMAT = new Intl.DateTimeFormat(APP_LOCALE, {
  day: "numeric",
  month: "short",
});

type Translate = ReturnType<typeof useTranslate>;

/** "Dirección · septiembre 2026" -- the panel's eyebrow (ticket description). */
function formatMonthYear(now: Date): string {
  return MONTH_YEAR_FORMAT.format(now);
}

const HeroCard = ({
  units,
  targetUnits,
  amountMxn,
  translate,
}: {
  units: number;
  targetUnits: number | null;
  amountMxn: number;
  translate: Translate;
}) => (
  <div className="flex flex-col gap-1 rounded-2xl bg-brand-navy p-4 text-white">
    <p className="text-base font-semibold opacity-80">
      {translate("crm.dashboard.direccion.hero_title")}
    </p>
    <p className="text-3xl font-bold tabular-nums">
      {targetUnits != null
        ? translate("crm.dashboard.direccion.hero_units_of_target", {
            // Plural (sale/sales) is chosen by the goal, not by how many
            // sales exist so far -- a goal of 5 with 1 sale reads "1 de 5
            // ventas", never "1 de 5 venta".
            smart_count: targetUnits,
            units,
            target: targetUnits,
          })
        : translate("crm.dashboard.direccion.hero_units", {
            smart_count: units,
          })}
    </p>
    <p className="text-base tabular-nums opacity-90">{formatMxn(amountMxn)}</p>
  </div>
);

const ProjectSalesSection = ({
  projectSales,
  translate,
}: {
  projectSales: ReturnType<typeof buildProjectSales>;
  translate: Translate;
}) => (
  <section className="flex flex-col gap-3">
    <h2 className="text-xl font-semibold text-muted-foreground">
      {translate("crm.dashboard.direccion.projects_title")}
    </h2>
    {projectSales.length === 0 ? (
      <p className="text-base text-muted-foreground">
        {translate("crm.dashboard.direccion.projects_empty")}
      </p>
    ) : (
      <div className="flex flex-col gap-2">
        {projectSales.map((project) => (
          <div
            key={project.projectId}
            className="flex items-center justify-between gap-3 rounded-2xl bg-card p-3"
          >
            <span className="text-base font-bold">{project.projectName}</span>
            <span className="shrink-0 text-base tabular-nums text-muted-foreground">
              {translate("crm.dashboard.direccion.project_sales_row", {
                smart_count: project.count,
                amount: formatMxn(project.amountMxn),
              })}
            </span>
          </div>
        ))}
      </div>
    )}
  </section>
);

export const DireccionPanel = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const { identity, isPending: isIdentityPending } = useGetIdentity();
  // Almikia Epic EP (TASK-107): the effective stage list (DATA CONTRACT EP
  // (8) -- the 10 system stages plus any direccion-created one), already
  // resolved and ordered by `useConfigurationLoader`. With the seeded
  // configuration this is exactly `ALMIKIA_STAGE_VALUES` in the same order.
  const { dealStages } = useConfigurationContext();
  // Ticket ASSUMPTION: a lead_manager gets the same panel, RLS-scoped to her
  // brazo, minus the VoBo slot (VoBo is direccion's own).
  const showVoBoSlot = identity?.role !== "lead_manager";
  const now = useMemo(() => new Date(), []);
  const period = useMemo(() => getCurrentMonthPeriod(now), [now]);
  const monthKey = useMemo(() => periodMonthKey(period), [period]);
  const monthStart = new Date(
    period.start.year,
    period.start.month - 1,
    period.start.day,
  );
  const monthEnd = new Date(
    period.end.year,
    period.end.month - 1,
    period.end.day,
  );
  // ASSUMPTION: narrows the "all-time closed sales" read below to a window
  // that should always contain this month's direct sales, so the DESC-sorted
  // `MAX_DIRECT_SALE_DEALS` cap keeps dropping only OLD sales, never current
  // ones, once the agency has more than 1,000 closed sales all-time. A sale
  // dated by `expected_closing_date` (DOMAIN RULE (a)) whose `updated_at` is
  // more than 3 months stale relative to that date would still be missed --
  // a real per-period aggregate (out of scope, no `supabase/` touch allowed
  // this ticket) is the only way to close that gap exactly.
  const directSalesLookbackStart = new Date(
    period.start.year,
    period.start.month - 1 - 3,
    period.start.day,
  )
    .toISOString()
    .slice(0, 10);

  // Onboarding stepper gating -- identical precedence to
  // AdvisorToday.tsx/MobileDashboard.tsx (ticket ASSUMPTION: "the onboarding
  // stepper keeps precedence for direccion on a CRM with no contacts").
  const {
    data: dataContact,
    total: totalContact,
    isPending: isPendingContact,
  } = useGetList<Contact>("contacts", { pagination: { page: 1, perPage: 1 } });
  const { total: totalContactNotes, isPending: isPendingContactNotes } =
    useGetList<ContactNote>("contact_notes", {
      pagination: { page: 1, perPage: 1 },
    });

  // DOMAIN RULE (a): archived leads still count as direct sales, so this
  // query never filters `archived_at` -- distinct from `openLeadsResult`
  // below, which deliberately does.
  const directSalesResult = useGetList<Deal>("deals", {
    pagination: { page: 1, perPage: MAX_DIRECT_SALE_DEALS },
    sort: { field: "updated_at", order: "DESC" },
    filter: {
      stage: CLOSED_WON_STAGE,
      "lead_type@in": `(${DIRECT_SALE_LEAD_TYPES.join(",")})`,
      "updated_at@gte": directSalesLookbackStart,
      "is_test_data@neq": true,
    },
  });

  const openLeadsResult = useGetList<Deal>("deals", {
    pagination: { page: 1, perPage: MAX_OPEN_LEADS },
    sort: { field: "id", order: "ASC" },
    filter: { "archived_at@is": null, "is_test_data@neq": true },
  });

  const goalsResult = useGetList<AdvisorGoal>("advisor_goals", {
    pagination: { page: 1, perPage: MAX_GOALS },
    filter: { scope: "advisor", period_month: monthKey },
  });

  const projectsResult = useGetList<Project>("projects", {
    pagination: { page: 1, perPage: MAX_PROJECTS },
    sort: { field: "name", order: "ASC" },
    filter: { "archived@eq": false },
  });

  // Exact per-stage counts (NFR: "count queries, not full lead lists"), one
  // per effective stage; `pipelineTotals`'s MXN sums still come from
  // `openLeadsResult`'s bounded read below.
  const stageValues = useMemo(
    () => dealStages.map((stage) => stage.value),
    [dealStages],
  );
  const stageOpenLeadCounts = useStageOpenLeadCounts(stageValues);

  const directSaleDeals = directSalesResult.data ?? [];
  const openLeads = openLeadsResult.data ?? [];
  const goals = goalsResult.data ?? [];
  const projects = projectsResult.data ?? [];

  const agencyGoalProgress = computeAgencyGoalProgress(
    directSaleDeals,
    goals,
    period,
  );
  const monthSales = useMemo(
    () => selectDirectSalesInPeriod(directSaleDeals, period),
    [directSaleDeals, period],
  );
  const monthUnits = monthSales.length;
  const monthAmountMxn = sumDirectSalesAmountMxn(monthSales);
  const targetUnits =
    agencyGoalProgress.status === "tracked"
      ? agencyGoalProgress.targetUnits
      : null;

  const pipelineTotals = useMemo(
    () =>
      selectDisplayedPipelineTotals(
        mergeExactStageCounts(
          buildPipelineTotals(openLeads, dealStages),
          stageOpenLeadCounts.counts,
        ),
        dealStages,
      ),
    [openLeads, dealStages, stageOpenLeadCounts.counts],
  );
  const posiPercent = useMemo(
    () => computePosiCompletePercent(openLeads),
    [openLeads],
  );
  // The MXN totals and the POSI percentage both read `openLeadsResult`'s
  // bounded `MAX_OPEN_LEADS` list -- when the RLS-visible set is larger than
  // that cap, `total` says so and the panel must show it, not silently
  // undercount (per-stage `count` stays exact regardless, from
  // `stageOpenLeadCounts`).
  const isPipelineAmountPartial =
    (openLeadsResult.total ?? 0) > openLeads.length;
  const projectSales = useMemo(
    () => buildProjectSales(projects, monthSales),
    [projects, monthSales],
  );

  const monthBarGoalPercent =
    targetUnits != null ? Math.min(100, (monthUnits / targetUnits) * 100) : 0;
  const unitsMissing =
    targetUnits != null ? Math.max(0, targetUnits - monthUnits) : 0;
  const daysLeft = daysLeftInPeriod(period, now);
  const daysLeftSentence = translate(
    "crm.dashboard.direccion.month_bar_days_left",
    { smart_count: daysLeft },
  );
  const monthBarSummary =
    targetUnits != null
      ? `${daysLeftSentence} · ${translate(
          "crm.dashboard.direccion.month_bar_need_sentence",
          {
            target: targetUnits,
            missing: unitsMissing,
            // Plural (sale/sales) is chosen by the goal, never by how many
            // units are still missing -- a goal of 5 missing 1 reads "5
            // ventas", never "5 venta".
            smart_count: targetUnits,
          },
        )}`
      : daysLeftSentence;

  const isDataPending =
    directSalesResult.isPending ||
    openLeadsResult.isPending ||
    goalsResult.isPending ||
    projectsResult.isPending ||
    stageOpenLeadCounts.isPending;
  const dataError =
    directSalesResult.error ??
    openLeadsResult.error ??
    goalsResult.error ??
    projectsResult.error ??
    stageOpenLeadCounts.error;

  const firstName = identity?.fullName?.split(" ")[0] ?? "";

  const content = (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-sm text-muted-foreground">
          {translate("crm.dashboard.direccion.eyebrow", {
            period: formatMonthYear(now),
          })}
        </p>
        <h1 className="text-2xl font-bold">
          {translate("crm.dashboard.direccion.greeting", { name: firstName })}
        </h1>
      </div>

      <MonthBar
        currentDate={now}
        monthStart={monthStart}
        monthEnd={monthEnd}
        goalPercent={monthBarGoalPercent}
        showGoalMarker={targetUnits != null}
        startLabel={MONTH_DATE_FORMAT.format(monthStart)}
        endLabel={MONTH_DATE_FORMAT.format(monthEnd)}
        summaryLabel={monthBarSummary}
      />

      {dataError ? (
        <ListErrorState
          message={translate("crm.common.list_error")}
          onRetry={() => {
            directSalesResult.refetch();
            openLeadsResult.refetch();
            goalsResult.refetch();
            projectsResult.refetch();
            stageOpenLeadCounts.refetch();
          }}
        />
      ) : isDataPending ? (
        <ListRowsSkeleton rows={6} columns={2} />
      ) : (
        <>
          <HeroCard
            units={monthUnits}
            targetUnits={targetUnits}
            amountMxn={monthAmountMxn}
            translate={translate}
          />

          <PipelineTotals
            totals={pipelineTotals}
            isAmountPartial={isPipelineAmountPartial}
          />

          <DireccionFunnelSection />

          <DireccionAlertsSection />

          <ProjectSalesSection
            projectSales={projectSales}
            translate={translate}
          />

          <section className="flex flex-col gap-2">
            <h2 className="text-xl font-semibold text-muted-foreground">
              {translate("crm.dashboard.direccion.posi_title")}
            </h2>
            <p className="text-base tabular-nums">
              {translate("crm.dashboard.direccion.posi_percent", {
                percent: posiPercent,
              })}
            </p>
            <ProgressBar
              value={posiPercent}
              max={100}
              label={translate("crm.dashboard.direccion.posi_bar_label")}
            />
            {isPipelineAmountPartial ? (
              <p className="text-sm text-muted-foreground">
                {translate("crm.dashboard.direccion.partial_data_note")}
              </p>
            ) : null}
          </section>

          <PreparedSlots showVoBoSlot={showVoBoSlot} />
        </>
      )}
    </div>
  );

  // Onboarding stepper gating -- identical to AdvisorToday.tsx/
  // MobileDashboard.tsx. Kept below `content` (not as an early return) so
  // both branches go through the same mobile-chrome wrapping below.
  let body: ReactNode = null;
  if (isIdentityPending || isPendingContact || isPendingContactNotes) {
    body = null;
  } else if (!totalContact) {
    body = <DashboardStepper step={1} />;
  } else if (!totalContactNotes) {
    body = <DashboardStepper step={2} contactId={dataContact?.[0]?.id} />;
  } else {
    body = content;
  }

  if (isMobile) {
    return (
      <>
        <MobileHomeHeader />
        <MobileContent>{body}</MobileContent>
      </>
    );
  }

  return body;
};
